import { KeyboardEvent, ReactNode } from 'react'
import { IAtlasObjectRef } from '@common/helpers/atlasUrls'
import cn from 'classnames'
import { useAtlasPreviewTrigger } from '../../../hooks/useAtlasPreviewTrigger'
import styles from './AtlasRecordChip.module.scss'

interface IProps {
  /** Null for a link with no record behind it, which navigates instead of previewing. */
  objectRef: IAtlasObjectRef | null
  /** Null: a record with no page (a teammate) is a button; with no record either, static. */
  url: string | null
  children: ReactNode
  className?: string
  title?: string
  /** Drop the pill and keep only `className`: a link whose record has not resolved yet. */
  isBare?: boolean
}

const handleButtonKeyDown = (event: KeyboardEvent<HTMLElement>) => {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  event.currentTarget.click()
}

/**
 * The pill every Atlas record reference draws — a pasted link, an @-mention, a timeline
 * target — so they look alike and behave alike: hover shows the record's card, click pins
 * it, a modifier click is left to the browser.
 */
const AtlasRecordChip = ({
  objectRef,
  url,
  children,
  className,
  title,
  isBare = false,
}: IProps) => {
  const triggerHandlers = useAtlasPreviewTrigger(objectRef, url ?? '')

  if (!url && objectRef) {
    return (
      <span
        role="button"
        tabIndex={0}
        className={cn(styles.chip, className)}
        title={title}
        onKeyDown={handleButtonKeyDown}
        {...triggerHandlers}
      >
        {children}
      </span>
    )
  }

  if (!url) {
    return (
      <span className={cn(styles.chip, styles.chipStatic, className)} title={title}>
        {children}
      </span>
    )
  }

  return (
    <a
      href={url}
      className={cn(!isBare && styles.chip, className)}
      title={title}
      {...triggerHandlers}
    >
      {children}
    </a>
  )
}

export default AtlasRecordChip
